import { initializeApp, getApps, getApp } from 'firebase/app';
import {
  getAuth,
  signInWithCustomToken,
  onAuthStateChanged,
} from 'firebase/auth';
import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL,
} from 'firebase/storage';
import axios from 'axios';
import { getWalletClient } from '@wagmi/core';

function getFirebaseApp() {
  if (getApps().length > 0) return getApp();
  return initializeApp({
    apiKey: process.env.REACT_APP_FIREBASE_HORUS_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_HORUS_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_HORUS_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_HORUS_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_HORUS_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_HORUS_APP_ID,
  });
}

const app = getFirebaseApp();
const storage = getStorage(app);
const auth = getAuth(app);

// ---- Non-custodial Firebase auth for storage uploads ----

let walletAuthPromise: Promise<void> | null = null;

function isFirebaseAuthenticated(): boolean {
  return auth.currentUser !== null;
}

/**
 * Ensure the current user has a Firebase Auth session.
 * Custodial users already have one from social login.
 * Non-custodial users get a custom token by signing a challenge with their wallet.
 */
export async function ensureFirebaseAuth(walletAddress?: string): Promise<void> {
  if (isFirebaseAuthenticated()) {
    console.log(`[Firebase Auth] Already authenticated (uid=${auth.currentUser?.uid})`);
    return;
  }
  if (!walletAddress) {
    console.warn('[Firebase Auth] Not authenticated and no wallet address provided — skipping');
    return;
  }

  // Deduplicate concurrent calls
  if (walletAuthPromise) {
    console.log('[Firebase Auth] Auth already in progress, waiting...');
    return walletAuthPromise;
  }

  console.log(`[Firebase Auth] Starting non-custodial auth for ${walletAddress}...`);
  walletAuthPromise = (async () => {
    try {
      const walletClient = await getWalletClient();
      if (!walletClient) {
        throw new Error('No wallet client available for signing.');
      }

      const challenge = `horus-chat-upload-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      console.log('[Firebase Auth] Signing challenge...');

      const signature = await walletClient.signMessage({
        account: walletAddress as `0x${string}`,
        message: challenge,
      });
      console.log('[Firebase Auth] Challenge signed, requesting token from backend...');

      const apiBase = process.env.REACT_APP_SOCIAL_WALLET_API_LOCAL || process.env.REACT_APP_SOCIAL_WALLET_API;

      const response = await axios.post(
        `${apiBase}/getChatUploadToken`,
        { walletAddress, signature, challenge },
        {
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': process.env.REACT_APP_HORUS_API_KEY || '',
          },
        }
      );

      const firebaseToken =
        response.data?.body
          ? JSON.parse(response.data.body).firebaseToken
          : response.data?.firebaseToken;

      if (!firebaseToken) {
        throw new Error('No Firebase token returned from backend');
      }

      console.log('[Firebase Auth] Signing in with custom token...');
      await signInWithCustomToken(auth, firebaseToken);
      console.log(`[Firebase Auth] ✓ Authenticated (uid=${auth.currentUser?.uid})`);
    } finally {
      walletAuthPromise = null;
    }
  })();

  return walletAuthPromise;
}

/**
 * Get the current Firebase Auth UID.
 * - Custodial users: Firebase user ID from social login
 * - Non-custodial users: wallet address (set as UID in custom token)
 *
 * Must be called after ensureFirebaseAuth().
 */
export function getFirebaseUid(): string | null {
  return auth.currentUser?.uid || null;
}

/**
 * Upload an encrypted file (Uint8Array) to Firebase Storage.
 * Returns the download URL.
 */
export async function uploadEncryptedFile(
  path: string,
  data: Uint8Array
): Promise<string> {
  console.log(`[Firebase Storage] Uploading to "${path}" (${(data.length / 1024).toFixed(1)} KB), auth uid=${auth.currentUser?.uid || 'NONE'}`);
  const storageRef = ref(storage, path);
  const blob = new Blob([data], { type: 'application/octet-stream' });

  try {
    await uploadBytes(storageRef, blob, {
      contentType: 'application/octet-stream',
      customMetadata: {
        encrypted: 'true',
      },
    });
    console.log(`[Firebase Storage] ✓ Upload successful for "${path}"`);
  } catch (err: any) {
    console.error(`[Firebase Storage] ✗ Upload FAILED for "${path}":`, err?.code, err?.message);
    throw err;
  }

  const downloadUrl = await getDownloadURL(storageRef);
  console.log(`[Firebase Storage] Download URL: ${downloadUrl.slice(0, 80)}...`);
  return downloadUrl;
}

/**
 * Download an encrypted file from Firebase Storage (or any URL).
 * Returns the raw Uint8Array.
 */
export async function downloadEncryptedFile(url: string): Promise<Uint8Array> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to download file: ${response.status}`);
  }
  const arrayBuffer = await response.arrayBuffer();
  return new Uint8Array(arrayBuffer);
}
